import React, { useContext } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useFormik, Form, FormikProvider } from 'formik';
import { NumericFormat } from 'react-number-format';
import {
  Button,
  TextField,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Divider,
  Chip,
  Tab,
  Box
} from '@mui/material';
import { TabContext, TabList, TabPanel, LoadingButton } from '@mui/lab';
// contexts
import { EventContext } from '../../../contexts/Event';
import { TicketsContext } from '../../../contexts/Tickets';
import { useAuth } from '../../../contexts/Auth';
import { SettingsContext } from '../../../contexts/Settings';
// services
import { createSection, updateSection } from '../../../services/Section';
import { updateTicketMany } from '../../../services/Ticket';
import { updateInventoryMany } from '../../../services/Inventory';

SectionFormDialog.propTypes = {
  openSectionFormDialog: PropTypes.bool.isRequired,
  setOpenSectionFormDialog: PropTypes.func.isRequired,
  section: PropTypes.object
};

export default function SectionFormDialog({
  openSectionFormDialog,
  setOpenSectionFormDialog,
  section
}) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { event } = useContext(EventContext);
  const { setSections } = useContext(TicketsContext);

  const sectionValues = {
    name: section?.name || '',
    description: section?.description || '',
    capacity: section?.capacity || '',
    seatsio: {
      category: {
        key: section?.seatsio?.category?.key || ''
      }
    },
    translation: {
      pt: {
        name: section?.translation?.pt?.name || '',
        description: section?.translation?.pt?.description || ''
      }
    }
  };

  const SectionSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    description: Yup.string(),
    capacity: Yup.number().integer().positive().required('Capacity is required'),
    seatsio: Yup.object().when([], {
      is: () => event?.bookingType === 'SEATSIO',
      then: Yup.object().shape({
        category: Yup.object().shape({
          key: Yup.string().required('Category Key is required')
        })
      })
    }),
    translation: Yup.object({
      pt: Yup.object({
        name: Yup.string(),
        description: Yup.string()
      })
    })
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: sectionValues,
    validationSchema: SectionSchema,
    onSubmit: async (values, actions) => {
      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        const payload = {
          name: values.name,
          description: values.description,
          capacity: values.capacity,
          single: false,
          event: {
            _id: event._id,
            name: event.name,
            translation: {
              pt: {
                name: event.translation.pt.name
              }
            }
          },
          translation: {
            pt: {
              name: values.translation.pt.name,
              description: values.translation.pt.description
            }
          }
        };

        if (event.bookingType === 'SEATSIO') {
          payload.seatsio = {
            category: {
              key: values.seatsio.category.key
            }
          };
        }

        if (section?._id) {
          await updateSection({ headers, payload, eventId: event._id, sectionId: section._id });

          // setSections((sections) =>
          //   sections.map((currentSection) => {
          //     if (currentSection._id === section._id) {
          //       return {
          //         ...section,
          //         name: values.name,
          //         description: values.description,
          //         capacity: values.capacity
          //       };
          //     }

          //     return currentSection;
          //   })
          // );

          // TODO: Atualizar usando kafka (provisório)
          const payloadServices = {
            event: {
              _id: event._id
            },
            section: {
              _id: section._id,
              name: values.name,
              description: values.description,
              single: section.single,
              capacity: values.capacity,
              translation: {
                pt: {
                  name: values.translation.pt.name,
                  description: values.translation.pt.description
                }
              }
            }
          };

          if (event.bookingType === 'SEATSIO') {
            payloadServices.section.seatsio = {
              category: {
                key: values.seatsio.category.key
              }
            };
          }

          await updateTicketMany({ headers, payload: payloadServices });
          await updateInventoryMany({ headers, payload: payloadServices });
          window.location.reload();
        } else {
          const section = await createSection({ headers, payload, eventId: event._id });
          setSections((sections) => [...sections, section]);
        }

        handleClose();
        actions.resetForm();
      } catch (err) {
        console.error(err);
        alert('An error occurred! Please, try again.');
      }
    }
  });

  const handleClose = () => {
    setOpenSectionFormDialog(false);
  };

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <Dialog open={openSectionFormDialog} fullWidth scroll="body" maxWidth="sm" sx={{ m: -2 }}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle sx={{ px: { xs: 2, sm: 3 } }}>
            {section?.name ? 'Edit' : 'Create'} Section
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
            <Stack spacing={2} pt={1}>
              <TextField
                fullWidth
                label="[EN] Name*"
                placeholder="i.e. Floor"
                {...getFieldProps('name')}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
              />

              <NumericFormat
                fullWidth
                label="Capacity*"
                {...getFieldProps('capacity')}
                error={Boolean(touched.capacity && errors.capacity)}
                helperText={touched.capacity && errors.capacity}
                decimalScale={0}
                customInput={TextField}
              />

              <TextField
                fullWidth
                label="[EN] Description (optional)"
                multiline
                rows={4}
                {...getFieldProps('description')}
                error={Boolean(touched.description && errors.description)}
                helperText={touched.description && errors.description}
              />

              {event?.bookingType === 'SEATSIO' && (
                <>
                  <Typography component="h2" variant="h6">
                    Seats Settings
                  </Typography>
                  <TextField
                    fullWidth
                    label="Category Key*"
                    {...getFieldProps('seatsio.category.key')}
                    error={Boolean(touched.seatsio?.category?.key && errors.seatsio?.category?.key)}
                    helperText={touched.seatsio?.category?.key && errors.seatsio?.category?.key}
                  />
                </>
              )}

              <Divider>
                <Chip label="Translations" size="small" />
              </Divider>

              <Box sx={{ width: '100%', typography: 'body1', marginTop: '0 !important' }}>
                <TabContext value="pt">
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList>
                      <Tab label="PT" value="pt" />
                    </TabList>
                  </Box>
                  <TabPanel value="pt">
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="[PT] Name"
                        variant="filled"
                        {...getFieldProps('translation.pt.name')}
                        error={Boolean(
                          touched.translation?.pt?.name && errors.translation?.pt?.name
                        )}
                        helperText={touched.translation?.pt?.name && errors.translation?.pt?.name}
                      />

                      <TextField
                        fullWidth
                        label="[PT] Description (optional)"
                        multiline
                        variant="filled"
                        rows={4}
                        {...getFieldProps('translation.pt.description')}
                        error={Boolean(
                          touched.translation?.pt?.description &&
                            errors.translation?.pt?.description
                        )}
                        helperText={
                          touched.translation?.pt?.description &&
                          errors.translation?.pt?.description
                        }
                      />
                    </Stack>
                  </TabPanel>
                </TabContext>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button size="large" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting}>
              Save
            </LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
}
